import { useCallback, useState } from "react";
import { DocumentData } from "../../components/documents/DocumentCollector/types";
import {
  usePublicComplianceGeneratePresignedUrlsMutation,
  usePublicComplianceUploadDocumentMutation,
} from "../../graphql/supergraph.index";
import { clientContext } from "../../utils/graphql_utils";
import { getUrlGeneratorInput, uploadFilesToS3 } from "../../utils/s3UploadClient";

export default function useGuestDocumentUpload(emailToken: string) {
  const [isUploading, setIsUploading] = useState(false);
  const [getPresignedUrlsForUpload] = usePublicComplianceGeneratePresignedUrlsMutation({
    context: clientContext(),
  });
  const [uploadDocument] = usePublicComplianceUploadDocumentMutation({
    context: clientContext(),
  });

  const uploadFiles = useCallback(
    (files: File[], retryCount = 3) => {
      const urlGeneratorInput = getUrlGeneratorInput(files);
      return getPresignedUrlsForUpload({
        context: clientContext(),
        variables: {
          emailToken: emailToken,
          input: urlGeneratorInput,
        },
      }).then((urlResponse) => {
        const presignedObjs = urlResponse.data?.publicComplianceGeneratePresignedUrls || [];
        const filesToUpload = files.map((file, index) => {
          return {
            generatedUrlObj: presignedObjs[index],
            file,
          };
        });
        return uploadFilesToS3(filesToUpload, retryCount);
      });
    },
    [getPresignedUrlsForUpload, emailToken],
  );

  /**
   * Uploads documents to S3 and registers them in the system
   *
   * Process:
   * 1. Extracts files from documents
   * 2. Uploads to S3
   * 3. Registers documents in the system
   * 4. Returns updated documents with assigned IDs
   *
   * @param documents - Documents containing files to upload
   * @returns Updated documents with system-assigned IDs
   */
  const uploadDocuments = useCallback(
    async (documents: DocumentData[]) => {
      // Early return for empty document array
      if (documents.length === 0) {
        return documents;
      }

      // Extract files using map and filter operations
      const filesToUpload = documents
        .map((document) => document.file?.file)
        .filter((file): file is File => file !== undefined);

      // If there are no files to upload, return the original documents
      if (filesToUpload.length === 0) {
        return documents;
      }

      // Now that we know we have files, set loading state
      setIsUploading(true);
      try {
        const uploadResponse = await uploadFiles(filesToUpload);

        // Create map of filename to document metadata
        const nameMetaMap = new Map(
          documents
            .filter((doc) => doc.file?.file)
            .map((doc) => [
              doc.file?.file.name,
              {
                baseType: doc.baseType || null,
                metadata: doc.subType ? [{ key: "type", value: doc.subType }] : [],
              },
            ]),
        );

        const documentInputs = uploadResponse.map((s3File) => ({
          contentType: s3File.file.type,
          fileName: s3File.file.name,
          s3Key: s3File.objectKey || "",
          documentPurpose: nameMetaMap.get(s3File.file.name)?.baseType || null,
          metadata: nameMetaMap.get(s3File.file.name)?.metadata,
        }));

        const uploadedDocumentsResponse = await uploadDocument({
          variables: {
            emailToken,
            documents: documentInputs,
          },
        });

        // Update document IDs in files based on the response
        const uploadedDocuments =
          uploadedDocumentsResponse.data?.publicComplianceUploadDocument || [];
        // Create a map of the created documents with id as the value and file name as the key
        const uploadedDocumentsMap = new Map(
          uploadedDocuments.map((document) => [document.name, document.id]),
        );
        const updatedDocuments = documents.map((document) => {
          if (!document.file?.file) return document;

          const documentId = uploadedDocumentsMap.get(document.file.file.name);
          if (documentId) {
            return {
              ...document,
              id: documentId,
            };
          }
          return document;
        });

        return updatedDocuments;
      } finally {
        setIsUploading(false);
      }
    },
    [emailToken, uploadDocument, uploadFiles],
  );

  return {
    uploadDocuments,
    loading: isUploading,
  };
}
